// File: src/components/EntryManageView.js
// An entrant editing or withdrawing their own entry, using the manage token
// from the link they were given.
//
// The token in the URL is the credential. It is unguessable, but it also lands
// in browser history and anywhere the entrant pastes it, so nothing sensitive
// is shown here beyond what they typed themselves.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateForDisplay } from '../utils/utils';
import { apiGet, apiPut, apiDelete } from '../services/api';
import EntryExtrasFields, { answersToPayload, answersFromEntry } from './EntryExtrasFields';

const EDITABLE = ['first_name', 'last_name', 'car_number',
                  'club', 'birth_year', 'email', 'phone', 'notes'];

const STATUS_CLASSES = {
  registered: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
  waitlisted: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-200',
  cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
};

function EntryManageView() {
  const { t } = useLanguage();
  const { entryToken } = useParams();
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState({});
  // Class id -> transponder. The number belongs to the class, not the entrant:
  // one person can run two cars in two classes.
  const [classTransponders, setClassTransponders] = useState({});
  // Changing your mind about lunch is exactly what this page is for, so the
  // extras are editable here for as long as entry is open.
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/public/entries/${entryToken}`);
      setEntry(data);
      setForm(Object.fromEntries(EDITABLE.map(f => [f, data[f] ?? ''])));
      setClassTransponders(Object.fromEntries(
        (data.classes || []).map(cls => [cls.id, cls.transponder_number ?? ''])
      ));
      setAnswers(answersFromEntry(data.answers));
      setError('');
    } catch (err) {
      // A wrong token is a 404 by design, so it is indistinguishable from an
      // entry that never existed.
      setError(err.status === 404 ? t('entryNotFound') : (err.message || t('entryFailed')));
    } finally {
      setLoading(false);
    }
  }, [entryToken, t]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      // The classes themselves cannot be changed here - only the transponder
      // declared for each - so they are resent exactly as they came.
      await apiPut(`/public/entries/${entryToken}`, {
        ...form,
        classes: (entry.classes || []).map(cls => ({
          event_class_id: cls.id,
          transponder_number: classTransponders[cls.id]
        })),
        answers: answersToPayload(answers, entry.questions || [])
      });
      setMessage(t('entryUpdated'));
      await load();
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('entryFailed'));
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async () => {
    if (!window.confirm(t('entryWithdrawConfirm'))) return;
    try {
      await apiDelete(`/public/entries/${entryToken}`);
      setMessage(t('entryWithdrawn'));
      await load();
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('entryFailed'));
    }
  };

  const backLink = (
    <Link to="/calendar" className="inline-flex items-center gap-2 text-ink-muted hover:text-ink mb-6">
      <ArrowLeft className="w-4 h-4" />
      {t('calendarBack')}
    </Link>
  );

  if (loading) return <div>{backLink}<p className="text-ink-muted">{t('loading')}</p></div>;

  if (!entry) {
    return (
      <div>
        {backLink}
        <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded">
          {error || t('entryNotFound')}
        </div>
      </div>
    );
  }

  const statusLabel = {
    registered: t('entryStatusRegistered'),
    waitlisted: t('entryStatusWaitlisted'),
    cancelled: t('entryStatusCancelled')
  }[entry.status] || entry.status;

  const field = (name, label, type = 'text') => (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">{label}</label>
      <input
        type={type}
        value={form[name] ?? ''}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
      />
    </div>
  );

  return (
    <div>
      {backLink}

      <div className="bg-paper border border-line rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-ink">{t('entryManageTitle')}</h1>
            <p className="text-ink-muted mt-1">
              {entry.event_name} · {formatDateForDisplay(entry.event_start_date)}
            </p>
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded ${STATUS_CLASSES[entry.status] || ''}`}>
            {statusLabel}
          </span>
        </div>

        {entry.classes && entry.classes.length > 0 && entry.status === 'cancelled' && (
          <div className="flex flex-wrap gap-2 mb-4">
            {entry.classes.map(cls => (
              <span key={cls.id} className="text-xs px-2 py-1 rounded bg-paper-alt text-ink-muted">
                {cls.name}
              </span>
            ))}
          </div>
        )}

        {message && (
          <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded mb-4">
            {message}
          </div>
        )}
        {error && (
          <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* A withdrawn entry stays visible - the entrant should be able to see
            that it worked - but there is nothing left to edit. */}
        {entry.status !== 'cancelled' && (
          <>
            <div className="space-y-4">
              {entry.classes && entry.classes.length > 0 && (
                <div>
                  <label className="block text-ink-muted mb-2 font-semibold">
                    {t('eventClasses')}
                  </label>
                  <p className="text-ink-muted text-sm mb-2">{t('entryTransponderPerClassHint')}</p>
                  <div className="space-y-2">
                    {entry.classes.map(cls => (
                      <div key={cls.id} className="flex items-center gap-2 bg-paper-alt px-3 py-2 rounded">
                        <span className="text-ink flex-1">{cls.name}</span>
                        <span className="text-ink-muted text-sm whitespace-nowrap">
                          {t('entryClassTransponder')}
                        </span>
                        <input
                          type="text"
                          value={classTransponders[cls.id] ?? ''}
                          onChange={(e) => setClassTransponders({
                            ...classTransponders, [cls.id]: e.target.value
                          })}
                          className="w-40 px-3 py-1.5 bg-paper border border-line rounded text-ink font-mono-num"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <EntryExtrasFields
                questions={entry.questions}
                answers={answers}
                onChange={setAnswers}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('first_name', t('entryFirstName'))}
                {field('last_name', t('entryLastName'))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('car_number', t('entryCarNumber'), 'number')}
                {field('club', t('entryClub'))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('email', t('entryEmail'), 'email')}
                {field('phone', t('entryPhone'))}
              </div>
              {field('notes', t('entryNotes'))}
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={save}
                disabled={saving}
                className="bg-accent hover:opacity-90 disabled:opacity-50 text-accent-ink px-6 py-2 rounded font-semibold flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {t('save')}
              </button>
              <button
                onClick={withdraw}
                className="bg-paper-alt hover:opacity-80 text-red-700 dark:text-red-400 px-6 py-2 rounded font-semibold flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {t('entryWithdraw')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EntryManageView;
